import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import BranchClosures from "../components/BranchClosures";
import BranchLocationModal from "../components/BranchLocationModal";
import BranchStatusBadge from "../components/BranchStatusBadge";
import GenderBadge from "../components/GenderBadge";
import UploadMediaModal from "../components/UploadMediaModal";
import { can } from "../services/permissions";
import { getCurrentUser } from "../services/authService";
import WorkingHours from "../components/WorkingHours";
import { apiErrorMessage } from "../services/api";
import {
  deleteBranchMedia,
  fetchBranch,
  fetchBranchMedia,
  reorderBranchMedia,
  setPrimaryBranchMedia,
} from "../services/branchService";
import { formatDate, formatDateTime } from "../services/datetime";
import { labelFor } from "../services/enumLabels";

export default function BranchDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  // A Google place linked or unlinked inside the settings modal. Held until it
  // closes: patching `branch` while it is open would reset the form.
  const googleChange = useRef(null);
  // Editing a branch's setup is a permission, not a role.
  const canEdit = can(getCurrentUser(), "branches.update");
  const [deletingId, setDeletingId] = useState(null);
  const [mediaError, setMediaError] = useState("");
  // The order last saved, while the user has unsaved moves; null when clean.
  const [savedOrder, setSavedOrder] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragId, setDragId] = useState(null);

  /*
   * Photos and videos are shown apart but share one order. A move inside one
   * group keeps that group in the slots it already occupies, so the two stay
   * interleaved exactly as before.
   */
  const reorderGroup = (type, groupIds) => {
    setBranch((b) => {
      const media = [...b.media];
      const slots = media.map((m, index) => (m.type === type ? index : -1)).filter((index) => index >= 0);
      const byId = Object.fromEntries(media.map((m) => [m.id, m]));
      slots.forEach((slot, n) => {
        media[slot] = byId[groupIds[n]];
      });
      return { ...b, media };
    });
    setSavedOrder((current) => current ?? branch.media);
  };

  const moveItem = (type, mediaId, delta) => {
    const ids = branch.media.filter((m) => m.type === type).map((m) => m.id);
    const from = ids.indexOf(mediaId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to], ids[from]];
    reorderGroup(type, ids);
  };

  const dropOn = (type, targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = branch.media.filter((m) => m.type === type).map((m) => m.id);
    if (!ids.includes(dragId)) return;
    const without = ids.filter((x) => x !== dragId);
    without.splice(without.indexOf(targetId) + (ids.indexOf(dragId) < ids.indexOf(targetId) ? 1 : 0), 0, dragId);
    reorderGroup(type, without);
    setDragId(null);
  };

  const saveOrder = async () => {
    setMediaError("");
    setSavingOrder(true);
    try {
      const media = await reorderBranchMedia(
        id,
        branch.media.map((m) => m.id)
      );
      setBranch((b) => ({ ...b, media }));
      setSavedOrder(null);
    } catch (err) {
      setMediaError(apiErrorMessage(err, t("media.orderFailed")));
    } finally {
      setSavingOrder(false);
    }
  };

  const resetOrder = () => {
    if (savedOrder) setBranch((b) => ({ ...b, media: savedOrder }));
    setSavedOrder(null);
  };

  const makePrimary = async (mediaId) => {
    setMediaError("");
    try {
      const media = await setPrimaryBranchMedia(id, mediaId);
      // Keep any unsaved order; only the flags come from the server.
      const primaryId = media.find((m) => m.is_primary)?.id;
      setBranch((b) => ({ ...b, media: b.media.map((m) => ({ ...m, is_primary: m.id === primaryId })) }));
    } catch (err) {
      setMediaError(apiErrorMessage(err, t("media.primaryFailed")));
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    setMediaError("");
    setDeletingId(mediaId);
    try {
      await deleteBranchMedia(id, mediaId);
      // Refetched, not filtered: deleting the cover promotes the next photo.
      const media = await fetchBranchMedia(id);
      setBranch((b) => ({ ...b, media }));
      setSavedOrder(null);
    } catch (err) {
      setMediaError(apiErrorMessage(err, t("media.deleteFailed")));
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const data = await fetchBranch(id);
        if (!cancelled) setBranch(data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.status === 404
              ? t("branchDetail.notFound")
              : apiErrorMessage(err, t("branchDetail.loadFailed"))
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, i18n.language, t]);

  if (loading) {
    return <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>;
  }

  if (error) {
    return (
      <div>
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
        <button type="button" onClick={() => navigate("/branches")} className="btn-secondary">
          {t("branchDetail.backToBranches")}
        </button>
      </div>
    );
  }

  const photos = (branch.media ?? []).filter((m) => m.type === "photo");
  const videos = (branch.media ?? []).filter((m) => m.type === "video");

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold">{branch.name}</h1>
            <BranchStatusBadge status={branch.status} />
            <GenderBadge gender={branch.gender} />
            {/* Read-only: VIP periods are set by platform admins. */}
            {branch.vip_until && (
              <span
                title={t("branchDetail.vipHint")}
                className="inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-[#F5C451] to-[#D4A017] px-2.5 py-0.5 text-xs font-bold text-[#3A2A00]"
              >
                <span aria-hidden="true">👑</span>
                {t("branchDetail.vipUntil", {
                  date: formatDate(String(branch.vip_until).slice(0, 10), i18n.language),
                })}
              </span>
            )}
          </div>
          <p className="text-sm text-ink-2">{branch.address}</p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <button type="button" onClick={() => setLocationOpen(true)} className="btn-primary">
              {t("branchDetail.editLocation")}
            </button>
          )}
          <Link to={`/branches/${id}/services`} className="btn-primary">
            {t("services.manage")}
          </Link>
          <Link to={`/branches/${id}/products`} className="btn-primary">
            {t("products.manage")}
          </Link>
          <Link to="/branches" className="btn-secondary">
            {t("common.back")}
          </Link>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        {/* Summary */}
        <div className="card p-5 lg:col-span-2">
          <div className="mb-4 flex items-center gap-3">
            {branch.logo ? (
              <img src={branch.logo} alt="" className="h-14 w-14 rounded-xl object-cover" />
            ) : (
              <div className="grid h-14 w-14 place-items-center rounded-xl bg-accent text-card-title font-bold text-accent-on">
                {branch.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate font-semibold">{branch.name}</p>
              <p className="truncate text-xs text-ink-2">{branch.address}</p>
            </div>
          </div>

          <dl className="space-y-2 text-sm">
            <Row label={t("branchDetail.id")}>
              <code className="break-all text-xs text-ink-2">{branch.id}</code>
            </Row>
            <Row label={t("branchDetail.manager")}>
              {branch.manager ? (
                <>
                  <span className="block">{branch.manager.name}</span>
                  <span className="block text-xs text-ink-2" dir="ltr">
                    {branch.manager.email}
                  </span>
                  {!branch.manager.is_active && (
                    <span className="mt-1 inline-block rounded-full bg-danger-wash px-2 py-1 text-xs font-medium text-danger-text">
                      {t("branchDetail.managerInactive")}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-ink-2">{t("branchDetail.noManager")}</span>
              )}
            </Row>
            <Row label={t("branchDetail.city")}>{branch.city?.name ?? "—"}</Row>
            <Row label={t("branchDetail.currency")}>
              {branch.currency ? `${branch.currency.name} (${branch.currency.code})` : "—"}
            </Row>
            <Row label={t("branchDetail.coordinates")}>
              {branch.latitude && branch.longitude ? (
                <a
                  href={`https://www.google.com/maps?q=${branch.latitude},${branch.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-text hover:underline"
                >
                  {branch.latitude}, {branch.longitude}
                </a>
              ) : (
                "—"
              )}
            </Row>
            <Row label={t("branchDetail.mapUrl")}>
              {branch.map_url ? (
                <a
                  href={branch.map_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-text hover:underline"
                >
                  {t("branchDetail.openInMaps")}
                </a>
              ) : (
                "—"
              )}
            </Row>
            <Row label={t("branchDetail.created")}>
              {branch.created_at ? formatDateTime(branch.created_at, i18n.language) : "—"}
            </Row>
            <Row label={t("branchDetail.updated")}>
              {branch.updated_at ? formatDateTime(branch.updated_at, i18n.language) : "—"}
            </Row>
          </dl>
        </div>

        {/* Business types + description */}
        <div className="card p-5 lg:col-span-3">
          <h2 className="mb-3 text-sm font-semibold">
            {t("branchDetail.businessTypes")}
            <span className="ms-2 rounded-full bg-raised px-2 py-1 text-xs text-ink-2">
              {branch.business_types?.length ?? 0}
            </span>
          </h2>

          {branch.business_types?.length ? (
            <div className="flex flex-wrap gap-2">
              {branch.business_types.map((type) => (
                <span
                  key={type.id}
                  className="rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text"
                >
                  {type.name}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink-2">{t("branchDetail.noBusinessTypes")}</p>
          )}

          <hr className="my-4 border-line" />

          <h2 className="mb-2 text-sm font-semibold">{t("branchDetail.description")}</h2>
          <p className="whitespace-pre-line text-sm text-ink-2">
            {branch.description || t("branchDetail.noDescription")}
          </p>
        </div>

        {/* Working hours */}
        <div className="lg:col-span-3">
          <WorkingHours
            branchId={id}
            workingHours={branch.working_hours ?? []}
            onSaved={(hours) => setBranch((b) => ({ ...b, working_hours: hours }))}
          />
        </div>

        {/* Closures & special dates */}
        <div className="lg:col-span-2">
          <BranchClosures branchId={id} />
        </div>

        {/* Media */}
        <div className="card p-5 lg:col-span-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">
              {t("media.title")}
              <span className="ms-2 rounded-full bg-raised px-2 py-1 text-xs text-ink-2">
                {branch.media?.length ?? 0}
              </span>
            </h2>
            <button type="button" onClick={() => setUploadOpen(true)} className="btn-primary">
              {t("media.add")}
            </button>
          </div>

          {mediaError && (
            <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
              {mediaError}
            </div>
          )}

          {branch.media?.length > 1 && (
            <p className="mb-3 text-xs text-ink-2">{t("media.orderHint")}</p>
          )}

          {savedOrder && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-raised px-3 py-2">
              <span className="text-sm">{t("media.orderChanged")}</span>
              <div className="flex gap-2">
                <button type="button" onClick={resetOrder} disabled={savingOrder} className="btn-secondary">
                  {t("media.resetOrder")}
                </button>
                <button type="button" onClick={saveOrder} disabled={savingOrder} className="btn-primary">
                  {savingOrder ? t("media.savingOrder") : t("media.saveOrder")}
                </button>
              </div>
            </div>
          )}

          {!branch.media?.length && (
            <p className="text-sm text-ink-2">{t("media.empty")}</p>
          )}

          {photos.length > 0 && (
            <>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-2">
                {t("media.photos", { count: photos.length })}
              </p>
              <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {photos.map((item, index) => (
                  <MediaTile
                    key={item.id}
                    item={item}
                    deleting={deletingId === item.id}
                    onDelete={handleDeleteMedia}
                    first={index === 0}
                    last={index === photos.length - 1}
                    onMove={(delta) => moveItem("photo", item.id, delta)}
                    onMakePrimary={() => makePrimary(item.id)}
                    dragging={dragId === item.id}
                    onDragStart={() => setDragId(item.id)}
                    onDrop={() => dropOn("photo", item.id)}
                    onDragEnd={() => setDragId(null)}
                  >
                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                      <img src={item.url} alt="" className="h-32 w-full object-cover" />
                    </a>
                  </MediaTile>
                ))}
              </div>
            </>
          )}

          {videos.length > 0 && (
            <>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-2">
                {t("media.videos", { count: videos.length })}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {videos.map((item, index) => (
                  <MediaTile
                    key={item.id}
                    item={item}
                    deleting={deletingId === item.id}
                    onDelete={handleDeleteMedia}
                    first={index === 0}
                    last={index === videos.length - 1}
                    onMove={(delta) => moveItem("video", item.id, delta)}
                    dragging={dragId === item.id}
                    onDragStart={() => setDragId(item.id)}
                    onDrop={() => dropOn("video", item.id)}
                    onDragEnd={() => setDragId(null)}
                  >
                    <video
                      src={item.url}
                      controls
                      preload="metadata"
                      className="h-40 w-full bg-black object-cover"
                    />
                  </MediaTile>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <BranchLocationModal
        branch={branch}
        open={locationOpen}
        onClose={() => {
          setLocationOpen(false);
          if (googleChange.current) setBranch((b) => ({ ...b, ...googleChange.current }));
          googleChange.current = null;
        }}
        onChanged={(updated) => {
          googleChange.current = updated;
        }}
        onSaved={(updated) => {
          setLocationOpen(false);
          googleChange.current = null;
          setBranch((b) => ({ ...b, ...updated }));
        }}
      />

      <UploadMediaModal
        branchId={id}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(media) => {
          // The server's list: new files last, cover flagged. Unsaved moves are dropped.
          setBranch((b) => ({ ...b, media }));
          setSavedOrder(null);
        }}
      />
    </div>
  );
}

/**
 * One media item: drag to reorder (or the move buttons, for keyboard and
 * touch), the cover badge / "Set as cover", and a delete control on hover.
 */
function MediaTile({
  item,
  children,
  deleting,
  onDelete,
  first,
  last,
  onMove,
  onMakePrimary,
  dragging,
  onDragStart,
  onDrop,
  onDragEnd,
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  return (
    <div
      draggable={!confirming}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        onDragStart?.();
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDrop?.();
      }}
      onDragEnd={onDragEnd}
      className={`group relative overflow-hidden rounded-lg border ${
        item.is_primary ? "border-accent ring-2 ring-accent" : "border-line"
      } ${dragging ? "opacity-50" : ""}`}
    >
      {children}

      {item.is_primary && (
        <span className="absolute start-1.5 top-1.5 rounded-full bg-accent px-2 py-0.5 text-caption font-semibold text-accent-on">
          {t("media.primary")}
        </span>
      )}

      <div className="flex items-center justify-between gap-1 border-t border-line bg-surface px-1.5 py-1">
        <div className="flex gap-1">
          <button
            type="button"
            disabled={first}
            onClick={() => onMove?.(-1)}
            aria-label={t("media.moveEarlier")}
            title={t("media.moveEarlier")}
            className="grid h-7 w-7 place-items-center rounded border border-line text-sm hover:bg-raised disabled:opacity-40"
          >
            <span aria-hidden="true" className="rtl:rotate-180">‹</span>
          </button>
          <button
            type="button"
            disabled={last}
            onClick={() => onMove?.(1)}
            aria-label={t("media.moveLater")}
            title={t("media.moveLater")}
            className="grid h-7 w-7 place-items-center rounded border border-line text-sm hover:bg-raised disabled:opacity-40"
          >
            <span aria-hidden="true" className="rtl:rotate-180">›</span>
          </button>
        </div>
        {onMakePrimary && !item.is_primary && (
          <button
            type="button"
            onClick={onMakePrimary}
            className="rounded px-2 py-1 text-caption font-semibold text-accent-text hover:bg-raised"
          >
            {t("media.makePrimary")}
          </button>
        )}
      </div>

      {confirming ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 p-2 text-center">
          <p className="text-xs text-white">
            {item.type === "photo" ? t("media.confirmPhoto") : t("media.confirmVideo")}
          </p>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={deleting}
              onClick={() => onDelete(item.id)}
              className="rounded bg-danger px-2 py-1 text-caption font-semibold text-danger-on hover:bg-danger-text disabled:border disabled:border-line-strong disabled:bg-raised disabled:text-ink-2"
            >
              {deleting ? t("media.deleting") : t("common.delete")}
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={() => setConfirming(false)}
              className="rounded bg-surface px-2 py-1 text-xs font-semibold text-ink hover:bg-raised"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          aria-label={`${t("common.delete")} ${labelFor("media_type", item.type)}`}
          className="absolute end-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white opacity-0 transition hover:bg-danger focus:opacity-100 group-hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3">
      <dt className="w-28 flex-shrink-0 text-ink-2">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}
